import React, { useEffect } from 'react';
import { useEditor, useValue } from 'tldraw';
import {
  collectTextAboutLinks,
  textAboutLeaderPoints,
} from '../utils/textAboutObject';

const VIEW_PAD = 48;
const TEXT_CLASS = 'board-text-about-bound';
const TARGET_CLASS = 'board-text-about-target';

function shapeDomId(el) {
  return el?.getAttribute?.('data-shape-id') || '';
}

function findShapeEl(container, shapeId) {
  if (!container || !shapeId) return null;
  return container.querySelector(
    `.tl-shape[data-shape-id="${CSS.escape(shapeId)}"]:not(.tl-shape-background)`
  );
}

function syncAboutHighlightDom(container, textIds, targetIds) {
  if (!container) return;
  const wantedText = new Set(textIds);
  const wantedTarget = new Set(targetIds);

  container.querySelectorAll(`.tl-shape.${TEXT_CLASS}`).forEach(el => {
    if (!wantedText.has(shapeDomId(el))) el.classList.remove(TEXT_CLASS);
  });
  container.querySelectorAll(`.tl-shape.${TARGET_CLASS}`).forEach(el => {
    if (!wantedTarget.has(shapeDomId(el))) el.classList.remove(TARGET_CLASS);
  });

  for (const id of wantedText) findShapeEl(container, id)?.classList.add(TEXT_CLASS);
  for (const id of wantedTarget) findShapeEl(container, id)?.classList.add(TARGET_CLASS);
}

function segmentInView(from, to, viewport) {
  if (!viewport) return true;
  const minX = Math.min(from.x, to.x);
  const maxX = Math.max(from.x, to.x);
  const minY = Math.min(from.y, to.y);
  const maxY = Math.max(from.y, to.y);
  return !(
    maxX < viewport.x - VIEW_PAD
    || minX > viewport.x + viewport.w + VIEW_PAD
    || maxY < viewport.y - VIEW_PAD
    || minY > viewport.y + viewport.h + VIEW_PAD
  );
}

/** True while a select-tool drag is moving shapes (or about to). */
function isMovingShapes(editor) {
  const path = String(editor.getPath?.() || '');
  return (
    path.includes('translating')
    || path.includes('pointing_shape')
    || Boolean(editor.inputs?.isDragging)
  );
}

function hotShapeIds(editor) {
  const hoveredId = editor.getHoveredShapeId?.() || null;
  const selected = editor.getSelectedShapeIds?.() || [];
  const hot = new Set(selected);
  if (hoveredId) hot.add(hoveredId);
  if (isMovingShapes(editor) && selected.length && editor.getShapeAndDescendantIds) {
    for (const id of editor.getShapeAndDescendantIds(selected)) {
      hot.add(id);
    }
  }
  return hot;
}

/**
 * Caption leader from the middle of nearby text to its object.
 * Visible while the text or that object is selected, hovered, or being dragged.
 */
export default function TextAboutObjectHint() {
  const editor = useEditor();

  const model = useValue(
    'text-about-object-links',
    () => {
      editor.getCamera?.();
      editor.getPath?.();
      // Pointer store record updates every drag frame (inputs Vec is mutated in place).
      editor.store?.get?.('pointer:pointer');

      const links = collectTextAboutLinks(editor);
      const viewport = editor.getViewportScreenBounds?.();
      const moving = isMovingShapes(editor);
      const hot = hotShapeIds(editor);
      const editingId = editor.getEditingShapeId?.() || null;

      const leaders = [];
      const boundTextIds = new Set();
      const hotTargetIds = new Set();

      for (const link of links) {
        if (!moving && link.textId === editingId) continue;
        if (!link.textBounds || !link.targetBounds) continue;

        const textHot = hot.has(link.textId);
        const targetHot = hot.has(link.targetId);
        if (!textHot && !targetHot) continue;

        const points = textAboutLeaderPoints(link.textBounds, link.targetBounds);
        const from = editor.pageToScreen(points.from);
        const to = editor.pageToScreen(points.to);
        if (!segmentInView(from, to, viewport)) continue;

        if (targetHot) boundTextIds.add(link.textId);
        if (textHot) hotTargetIds.add(link.targetId);

        const length = Math.hypot(to.x - from.x, to.y - from.y);
        leaders.push({
          textId: link.textId,
          from,
          to,
          length,
        });
      }

      return {
        leaders,
        moving,
        boundTextIds: [...boundTextIds],
        hotTargetIds: [...hotTargetIds],
      };
    },
    [editor]
  );

  useEffect(() => {
    const container = editor.getContainer?.() || null;
    syncAboutHighlightDom(container, model.boundTextIds, model.hotTargetIds);
    return () => syncAboutHighlightDom(container, [], []);
  }, [editor, model.boundTextIds, model.hotTargetIds]);

  if (!model.leaders.length) return null;

  const minLength = model.moving ? 1 : 6;

  return (
    <svg
      className={`board-text-about-overlay${model.moving ? ' is-moving' : ''}`}
      aria-hidden="true"
    >
      {model.leaders.map(leader => (
        leader.length > minLength ? (
          <line
            key={leader.textId}
            x1={leader.from.x}
            y1={leader.from.y}
            x2={leader.to.x}
            y2={leader.to.y}
          />
        ) : null
      ))}
    </svg>
  );
}
